import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import { Tooltip } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import React from 'react';
import CardWrapper from 'components/Card/CardWrapper';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';
import { Trans } from 'react-i18next';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import EXTERNAL_URLS from 'constants/externalUrls';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import makeStyles from '@material-ui/styles/makeStyles';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const useStyles = makeStyles({
  ...extendedFormsStyle,
  ...buttonsStyle,
  labelWrapper: {
    display: 'flex',
  },
  tooltipWrapper: {
    marginLeft: '10px',
    display: 'inline-block',
  },
  imagesWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    textAlign: 'center',
  },
  tooltipList: {
    marginBlockStart: '0',
    marginBlockEnd: '0',
    paddingInlineStart: '1.5em',
  },
});

export const withOrderFormBannerDefaults = module => ({
  enabled: false,
  title: null,
  description: null,
  bannerDesktop: null,
  bannerMobile: null,
  '@resources': {},
  ...module,
});

export const BannerSettingsFields = ({
  t,
  module,
  onChange,
  disabled = false,
  isStacked = false,
}) => {
  const classes = useStyles();

  const getPreviewUrl = fieldName =>
    module?.['@resources']?.[module?.[fieldName]]?.contentUrl ?? null;

  const handleInputChange = ({ target: { name, value } }) =>
    onChange({ ...module, [name]: value });

  const getImage = (fieldName, data) =>
    onChange({
      ...module,
      [fieldName]: data?.['@id'] || null,
      '@resources': {
        ...module?.['@resources'],
        ...(data?.['@id'] ? { [data['@id']]: data } : {}),
      },
    });

  const removeImage = fieldName => onChange({ ...module, [fieldName]: null });

  const textFields = (
    <>
      <FormTextInput
        multiline={true}
        rows={3}
        rowsMax={10}
        label={
          <div className={classes.labelWrapper}>
            <span>{t('brandCfg.captionInBanner')}</span>
            <div className={classes.tooltipWrapper}>
              <Tooltip
                title={
                  <>
                    <p>{t('brandCfg.captionInBannerTooltip')}</p>
                    {`${t(
                      'brandCfg.etc'
                    )} <div style="margin: 30px; max-width: 50%">${t(
                      'brandCfg.captionInBannerTooltip.example'
                    )}</div>`}
                  </>
                }
                placement="right"
              >
                <Info fontSize="small" />
              </Tooltip>
            </div>
          </div>
        }
        classes={classes}
        name="title"
        value={module?.title ?? ''}
        handleChange={handleInputChange}
        inputSize={12}
        maxLength={160}
        disabled={disabled}
      />
      <FormTextInput
        label={
          <div className={classes.labelWrapper}>
            <span>{t('brandCfg.textUnderCaption')}</span>
            <div className={classes.tooltipWrapper}>
              <Tooltip
                title={
                  <>
                    <p>{t('brandCfg.captionInBannerTooltip')}</p>
                    {`${t(
                      'brandCfg.etc'
                    )} <div style="margin: 30px; max-width: 50%">${t(
                      'brandCfg.captionInBannerTooltip.example'
                    )}</div>`}
                  </>
                }
                placement="right"
              >
                <Info fontSize="small" />
              </Tooltip>
            </div>
          </div>
        }
        classes={classes}
        name="description"
        value={module?.description ?? ''}
        handleChange={handleInputChange}
        inputSize={12}
        maxLength={1000}
        rows={3}
        rowsMax={20}
        multiline={true}
        disabled={disabled}
      />
    </>
  );

  const enabledCheckbox = (
    <LabeledCheckbox
      label={t('brandCfg.infoBannerShow.short')}
      checked={Boolean(module?.enabled)}
      onChange={() => onChange({ ...module, enabled: !module?.enabled })}
      disabled={disabled}
    />
  );

  const desktopBanner = (
    <FormImageUpload
      stateName="bannerDesktop"
      buttonText={t('common.shared.select')}
      getImage={getImage}
      imgId="desktop-banner"
      label={t('brandCfg.bannerPanorama.label')}
      labelAdditionalContent={
        <InfoWithTooltip
          title={
            <Trans
              i18nKey="brandCfg.bannerPanorama.tooltip"
              components={{
                ul: <ul className={classes.tooltipList} />,
                li: <li />,
              }}
            />
          }
          link={EXTERNAL_URLS.bannerImage}
          linkText={t('brandCfg.bannerTooltip.linkText')}
        />
      }
      removeImage={removeImage}
      previewUrl={getPreviewUrl('bannerDesktop')}
      maxFileSize={MAX_FILE_SIZE}
      proportionsConstraint={[1, 3]}
      isDisabled={disabled}
    />
  );

  const mobileBanner = (
    <FormImageUpload
      stateName="bannerMobile"
      buttonText={t('common.shared.select')}
      getImage={getImage}
      imgId="mobile-banner"
      label={t('brandCfg.bannerPortrait.label')}
      labelAdditionalContent={
        <InfoIconTooltip
          title={
            <Trans
              i18nKey="brandCfg.bannerPortrait.tooltip"
              components={{
                ul: <ul className={classes.tooltipList} />,
                li: <li />,
              }}
            />
          }
        />
      }
      removeImage={removeImage}
      previewUrl={getPreviewUrl('bannerMobile')}
      maxFileSize={MAX_FILE_SIZE}
      proportionsConstraint={[5, 4]}
      isDisabled={disabled}
    />
  );

  if (isStacked) {
    return (
      <GridContainer>
        <GridItem xs={12}>{enabledCheckbox}</GridItem>
        <GridItem md={6}>{desktopBanner}</GridItem>
        <GridItem md={6}>{mobileBanner}</GridItem>
        <GridItem md={6}>{textFields}</GridItem>
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <GridItem md={6}>{textFields}</GridItem>
      <GridItem md={6}>
        {enabledCheckbox}
        <div className={classes.imagesWrapper}>
          <div>{desktopBanner}</div>
          <div>{mobileBanner}</div>
        </div>
      </GridItem>
    </GridContainer>
  );
};

const BannerSettings = ({ t, module, onChange }) => (
  <CardWrapper title={t('brandCfg.view.orderForm')}>
    <CardBody>
      <BannerSettingsFields t={t} module={module} onChange={onChange} />
    </CardBody>
  </CardWrapper>
);

export default BannerSettings;
